import { add, differenceInMinutes, sub } from 'date-fns';
import * as uuid from 'uuid';
import { groupBy } from 'lodash';
import { IEvent, OptType } from './interface';
import { SEPARATOR } from '../commonConst';
import { IStandbyPeriod } from '../standby-period/interface';

export interface ICalendarEvent {
    id: number;
    resourceId: any;
    start: Date;
    end: Date;
    title: string;
    classNames?: string[];
}

export const getEventPrograms = (event: IEvent) => {
    if (event.event_source?.includes('type=bid')) {
        return ['PRICE RESPONSE'];
    }

    if (event.event_source?.includes('type=clean_response')) {
        return ['CLEAN RESPONSE'];
    }

    return event?.event_context?.programs || [];
};

export const groupEvents = (events: IEvent[]): GroupedEvent[] => {
    const groupedEvents = groupBy(events, event => {
        const programs = getEventPrograms(event);

        return `${event.event_batch || uuid.v4()}${SEPARATOR}${programs}`;
    });

    return Object.entries(groupedEvents).map(([key, eventList]) => {
        // Sort events by start time
        const sortedList = eventList.sort((a, b) => {
            return new Date(a.event_datetime_start).getTime() - new Date(b.event_datetime_start).getTime();
        });

        return new GroupedEvent(sortedList);
    });
};

export const toCalendarEvents = (events: IEvent[], calendarView: string): ICalendarEvent[] => {
    const calendarEvents: ICalendarEvent[] = [];

    if (['dayGridMonth', 'dayGridWeek'].includes(calendarView)) {
        const groupedEventsForGridView = groupEvents(events);

        calendarEvents.push(...groupedEventsForGridView);
    } else {
        events.forEach(event => {
            if (event.event_pre_duration > 0) {
                const preEvent = new PreEvent(event);
                calendarEvents.push(preEvent);
            }

            if (event.event_secondary_duration > 0) {
                const secondaryEvent = new SecondaryEvent(event);
                calendarEvents.push(secondaryEvent);
            }

            if (event.event_duration > 0) {
                const normalEvent = new Event(event);
                calendarEvents.push(normalEvent);
            }

            if (event.event_post_duration > 0) {
                const postEvent = new PostEvent(event);
                calendarEvents.push(postEvent);
            }
        });
    }
    return calendarEvents;
};

class BaseEvent implements ICalendarEvent {
    id: number;
    resourceId: any;
    start: Date;
    end: Date;
    title: string;
    extendedProps?: any;
    classNames: string[] = [];

    constructor(event: IEvent) {
        this.id = event.event_id;
        this.resourceId = event.site_id;
        this.title = '';
        this.start = new Date(event.event_datetime_start);
        this.end = add(new Date(event.event_datetime_start), { minutes: event.event_duration });
        this.addClass('event');

        if (event.event_cancelled) {
            this.addClass('canceled-event');
        } else if (event.event_opt_type === OptType.OPT_OUT) {
            this.addClass('optout-event');
        }

        if (event.event_source?.includes('type=bid')) {
            this.addClass('price-response-event');
        } else if (event.event_source?.includes('type=clean_response')) {
            this.addClass('clean-response-event');
        }

        if (event.event_test) {
            this.addClass('test-event');
        }

        if (event.no_end_time) {
            this.addClass('no-end-time');
        }

        // if (isCoincidentPeak) {
        //     this.addClass('coincident-peak-event');
        // }
    }

    protected addClass(name: string): void {
        this.classNames.push(name);
    }

    protected generateTitle(event: IEvent) {
        let titleParts: string[] = [];

        if (event.event_test) {
            titleParts.push('COMMUNICATIONS TEST');
        }

        if (event.event_emergency_generator_allowed) {
            titleParts.push('EMERGENCY');
        }

        if (event.event_voluntary) {
            titleParts.push('VOLUNTARY');
        }

        if (event.event_source?.includes('type=bid')) {
            titleParts = ['PRICE RESPONSE'];
        }

        if (event.event_source?.includes('type=clean_response')) {
            titleParts = ['CLEAN RESPONSE'];
        }

        if (event.no_end_time) {
            titleParts = ['NO EVENT END TIME YET'];
        }

        // if (isCoincidentPeak) {
        //     titleParts = ['COINCIDENT PEAK'];
        // }

        return titleParts.join(' & ');
    }
}

class Event extends BaseEvent {
    constructor(event: IEvent) {
        super(event);
        this.title = this.generateTitle(event);
        this.start = new Date(event.event_datetime_start);
        this.end = add(new Date(event.event_datetime_start), { minutes: event.event_duration });
    }
}

class PreEvent extends BaseEvent {
    constructor(event: IEvent) {
        super(event);
        this.start = sub(new Date(event.event_datetime_start), { minutes: event.event_pre_duration });
        this.end = new Date(event.event_datetime_start);
        this.addClass('pre-event');
    }
}

class PostEvent extends BaseEvent {
    constructor(event: IEvent) {
        super(event);
        this.start = add(new Date(event.event_datetime_start), { minutes: event.event_duration });
        this.end = add(this.start, { minutes: event.event_post_duration });
        this.addClass('post-event');
    }
}

class SecondaryEvent extends BaseEvent {
    constructor(event: IEvent) {
        super(event);
        this.start = sub(new Date(event.event_datetime_start), { minutes: event.event_secondary_duration });
        this.end = new Date(event.event_datetime_start);
        this.addClass('secondary-event');
    }
}

export class GroupedEvent extends BaseEvent {
    constructor(events: IEvent[]) {
        const event = events[0];
        super(event);

        const startTimeSet = new Set(events.map(e => e.event_datetime_start));

        this.extendedProps = {
            duration: event.event_duration,
            noEndTime: event.no_end_time,
            sitesCount: events.length,
            startTimeArray: [...startTimeSet],
        };
        this.title = getEventPrograms(event).join(', ');
    }
}

const getStandbyPeriodProgram = (standbyPeriod: IStandbyPeriod) => {
    return standbyPeriod?.source?.program?.name || '';
};

const groupStandbyPeriods = (standbyPeriods: IStandbyPeriod[]) => {
    const groupedStandbyPeriods = groupBy(standbyPeriods, standbyPeriod => {
        const program = getStandbyPeriodProgram(standbyPeriod);
        return `${standbyPeriod.batch_uuid}${SEPARATOR}${program}`;
    });

    return Object.entries(groupedStandbyPeriods).map(([key, standbyPeriodList]) => {
        // Sort standby periods by start time
        const sortedList = standbyPeriodList.sort((a, b) => {
            return new Date(a.datetime_start).getTime() - new Date(b.datetime_start).getTime();
        });

        return new GroupedStandbyPeriod(sortedList);
    });
};

export const toCalendarEventsFromStandbyPeriod = (standbyPeriods: IStandbyPeriod[], calendarView: string): ICalendarEvent[] => {
    const calendarEvents: ICalendarEvent[] = [];

    if (['dayGridMonth', 'dayGridWeek'].includes(calendarView)) {
        const groupedStandbyPeriodsForGridView = groupStandbyPeriods(standbyPeriods);

        calendarEvents.push(...groupedStandbyPeriodsForGridView);
    } else {
        standbyPeriods.forEach(standbyPeriod => {
            const normalStandbyPeriod = new StandbyPeriod(standbyPeriod);

            calendarEvents.push(normalStandbyPeriod);
        });
    }
    return calendarEvents;
};

class BaseStandbyPeriod implements ICalendarEvent {
    id: number;
    resourceId: any;
    start: Date;
    end: Date;
    title: string;
    extendedProps?: any;
    classNames: string[] = [];

    constructor(standbyPeriod: IStandbyPeriod) {
        this.id = standbyPeriod.id;
        this.resourceId = standbyPeriod.site_id;
        this.title = '';
        this.start = new Date(standbyPeriod.datetime_start);
        this.end = new Date(standbyPeriod.datetime_end);
        this.addClass('event');
        this.addClass('standby-period');

        if (standbyPeriod.is_cancelled) {
            this.addClass('canceled-event');
        }
    }

    protected addClass(name: string): void {
        this.classNames.push(name);
    }

    protected generateTitle() {
        return 'STANDBY PERIOD';
    }
}

class StandbyPeriod extends BaseStandbyPeriod {
    constructor(standbyPeriod: IStandbyPeriod) {
        super(standbyPeriod);
        this.title = this.generateTitle();
    }
}

class GroupedStandbyPeriod extends BaseStandbyPeriod {
    constructor(standbyPeriods: IStandbyPeriod[]) {
        const standbyPeriod = standbyPeriods[0];
        super(standbyPeriod);

        const startTimeSet = new Set(standbyPeriods.map(i => i.datetime_start));

        this.extendedProps = {
            standbyPeriodDuration: differenceInMinutes(this.end, this.start),
            sitesCount: standbyPeriods.length,
            startTimeArray: [...startTimeSet],
        };
        this.title = getStandbyPeriodProgram(standbyPeriod);
    }
}
